import React, { useState } from 'react';
import { Button, CircularProgress, FormControl, Grid2, OutlinedInput } from '@mui/material';
import { CloseIcon, UploadIcon } from '../../shared/components/AppIcons';
import { makeStyles } from '@mui/styles';
import { useAuthentication } from '../../auth/services/auth.provider';
import { useNavigate } from 'react-router';
import Toaster from '../../shared/components/Toaster';
import UserService from '../services/user.services';
import { logout } from '../../auth/services/auth.services';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';

const useStyles = makeStyles({
  header: {
    fontSize: '28px',
    color: '#303340',
    paddingLeft: '24px',
  },
  container: {
    width: '728px',
    textAlign: 'center',
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid #E1E3E8',
    padding: '22px',
    gap: '16px',
    '&.personal-info': {
      width: '400px',
      height: 'auto',
    },
    '&.account-info': {
      width: '400px',
      height: '151px',
    },
    '&.security': {
      height: '313px',
    },
  },
  info: {
    fontSize: '16px',
    color: '#939393',
  },
  icon: {
    fontSize: '70px !important',
  },
  uploadNewPicButton: {
    textTransform: 'none !important',
    borderColor: '#E1E3E8 !important',
    color: '#5F6774 !important',
    fontFamily: 'Lato !important',
    width: '164px !important',
    height: '40px !important',
    marginRight: '8px !important',
    '& .MuiButton-startIcon': {
      margin: '0px !important',
    },
  },
  close: {
    width: '36px',
    height: '36px',
    fontSize: '36px !important',
  },
  removeButton: {
    width: '130px !important',
    height: '40px !important',
    color: '#5F6774 !important',
    fontFamily: 'Lato !important',
    borderColor: '#E1E3E8 !important',
    '& .MuiButton-startIcon': {
      margin: '0px !important',
    },
  },
  text: {
    fontSize: '12px',
    alignSelf: 'flex-start',
    color: '#30343F',
  },
  outlineInputField: {
    height: '40px',
    fontFamily: 'Lato !important',
    fontSize: '14px !important',

    // Default border
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
      color: '#E1E3E8 !important',
    },
    '&.activeText input': {
      color: '#5F6774 !important',
    },
  },
  save: {
    width: '135px',
    height: '38px',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    fontSize: '18px !important',
    boxShadow: 'none !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '&.disable': {
      backgroundColor: '#848080 !important',
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
});

const EditProfile: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const auth = useAuthentication();
  const user = auth.currentUser;
  const [curPassword, setCurPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [show, setShow] = React.useState<boolean>(false);
  const [toasterMessage, setToasterMessage] = useState<string>('');
  const [toasterMessageType, setToasterMessageType] = useState<string>('');
  const [preview, setPreview] = useState<string>(`${user?.profileImageURL}?raw=1` || '');
  const [file, setFile] = useState<File | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getToaster = (actionString: string) => (
    <Toaster messageType={actionString} show={show} setShow={setShow} message={`${toasterMessage}`} />
  );

  const handleProfileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileObj = event.target.files?.[0];
    if (!fileObj) return;

    if (!fileObj.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // 🔹 Show preview immediately
    const previewUrl = URL.createObjectURL(fileObj);
    setPreview(previewUrl);
    setFile(fileObj);
  };

  const onClick = () => {
    document?.getElementById('file')?.click();
  };

  const handleRemoveProfilePicture = async () => {
    setPreview('');
    setFile(null);
    setShowConfirmationModal(false);
  };

  const logOut = () => {
    logout().then(() => {
      auth.logout();
      window.location.href = '/login';
    });
  };

  const showToaster = (type: string, message: string) => {
    setToasterMessageType(type);
    setToasterMessage(message);
    setShow(true);
  };

  const updateProfile = async () => {
    if (!user) return;
    setIsLoading(true);

    const isPasswordChange = curPassword || newPassword || confirmPassword;

    if (isPasswordChange && newPassword !== confirmPassword) {
      showToaster('failure', 'New password and confirm password do not match.');
      return;
    }

    try {
      // Upload profile picture
      if (file) {
        const formData = new FormData();
        formData.append('newProfilePicture', file);
        await UserService.updateUserProfilePic(user.id, formData, {
          headers: { 'content-type': 'multipart/form-data' },
        });
        showToaster('success', 'Profile picture updated successfully.');
        navigate('/dashboard');
      } else if (preview === null) {
        await UserService.removeProfilePic(user.id);
        showToaster('success', 'Profile picture removed successfully.');
        navigate('/dashboard');
      }

      // Update password (only if changed)
      if (isPasswordChange) {
        await UserService.updateUserPassword(user.id, {
          currentPassword: curPassword,
          newPassword,
        });
        showToaster('success', 'Password updated successfully.');
        logOut();
      }
    } catch (error) {
      showToaster('failure', error?.response?.data?.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid2 container direction="column">
      <Grid2 height="40px">
        <span className={classes.header}>Edit Profile</span>
      </Grid2>
      <Grid2 container direction="row" ml={1}>
        {/* Static Data */}
        <Grid2 container direction="column">
          {/* Personal Details(readOnly) */}
          <Grid2 container className={`${classes.container} personal-info`} mr={3} pt={3} mt={4}>
            <Grid2 container width="100%" justifyContent="flex-start">
              <span style={{ fontWeight: 'bold', fontFamily: 'Lato', fontSize: '16px', color: '#111827' }}>
                Personal Information
              </span>
            </Grid2>
            <Grid2 container mt={1} direction="column" width="100%">
              <Grid2 container direction="column">
                <label htmlFor="inputFirstName" className={classes.text}>
                  First Name
                </label>
                <FormControl>
                  <OutlinedInput
                    className={classes.outlineInputField}
                    placeholder="First Name"
                    value={user?.firstName}
                  />
                </FormControl>
              </Grid2>
              <Grid2 container direction="column" mt={1}>
                <label htmlFor="inputLastName" className={classes.text}>
                  Last Name
                </label>
                <FormControl>
                  <OutlinedInput className={classes.outlineInputField} placeholder="Last Name" value={user?.lastName} />
                </FormControl>
              </Grid2>
              <Grid2 container direction="column" mt={1}>
                <label htmlFor="inputUsername" className={classes.text}>
                  Username
                </label>
                <FormControl>
                  <OutlinedInput className={classes.outlineInputField} placeholder="Username" value={user?.username} />
                </FormControl>
              </Grid2>
              <Grid2 container direction="column" mt={1}>
                <label htmlFor="inputEmail" className={classes.text}>
                  Email
                </label>
                <FormControl>
                  <OutlinedInput className={classes.outlineInputField} placeholder="email" value={user?.email} />
                </FormControl>
              </Grid2>
            </Grid2>
          </Grid2>
          {/* Account Info(readOnly) */}
          <Grid2
            container
            direction="row"
            alignItems="flex-start"
            className={`${classes.container} account-info`}
            mr={3}
            pt={3}
            mt={3}
          >
            <Grid2 container width="100%" justifyContent="flex-start">
              <span style={{ fontWeight: 'bold', fontFamily: 'Lato', fontSize: '16px', color: '#111827' }}>
                Account Settings
              </span>
            </Grid2>
            <Grid2 container direction="row" alignItems="center" mt={1} width="100%">
              <Grid2 container direction="column" width="100%">
                <label htmlFor="inputRole" className={classes.text}>
                  Role
                </label>
                <FormControl>
                  <OutlinedInput
                    fullWidth
                    className={classes.outlineInputField}
                    placeholder="Role"
                    value={user?.role.name}
                  />
                </FormControl>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
        {/* Editable fields */}
        <Grid2 container direction="column">
          {getToaster(toasterMessageType)}
          <Grid2 container direction="column" mt={3}>
            {/* Profile Picture */}
            <Grid2 container direction="row" alignItems="flex-start" className={classes.container} mr={3} pt={3}>
              <input
                id="file"
                type="file"
                accept="image/*"
                multiple={false}
                onChange={handleProfileChange}
                style={{ display: 'none' }}
              />

              <Grid2 container width="100%" justifyContent="flex-start">
                <label
                  htmlFor="file"
                  style={{ fontWeight: 'bold', fontFamily: 'Lato', fontSize: '16px', color: '#111827' }}
                >
                  Profile Picture
                </label>
              </Grid2>

              <Grid2 container direction="row" alignItems="center" gap={3} mt={1}>
                <Grid2>
                  {console.log(preview)}
                  <img
                    src={preview}
                    alt="Profile"
                    width="84"
                    height="84"
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                  />
                </Grid2>

                <Grid2 container direction="column" className={classes.info}>
                  <Grid2>
                    <span>Upload a new profile picture. Recommended size: 400x400px</span>
                  </Grid2>

                  <Grid2 container mt={2}>
                    <Button
                      size="small"
                      startIcon={<UploadIcon viewBox="10 3 36 36" sx={{ width: '36px', height: '36px' }} />}
                      variant="outlined"
                      onClick={() => onClick()}
                      className={classes.uploadNewPicButton}
                    >
                      <span style={{ fontSize: '18px', color: '#5F6774' }}>Upload New</span>
                    </Button>

                    <Button
                      size="small"
                      startIcon={<CloseIcon viewBox="5 3 36 36" className={classes.close} />}
                      variant="outlined"
                      className={classes.removeButton}
                      onClick={() => setShowConfirmationModal(true)}
                    >
                      <span style={{ fontSize: '18px', color: '#5F6774' }}>Remove</span>
                    </Button>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Grid2>

            {/* Security */}
            <Grid2
              container
              direction="row"
              alignItems="flex-start"
              className={`${classes.container} security`}
              mr={3}
              pt={3}
              mt={3}
            >
              <Grid2 container width="100%" justifyContent="flex-start">
                <span style={{ fontWeight: 'bold', fontFamily: 'Lato', fontSize: '16px', color: '#111827' }}>
                  Security
                </span>
              </Grid2>
              <Grid2 container direction="column" gap={3} mt={1} width="100%">
                <Grid2 container direction="column" mr={1}>
                  <label htmlFor="inputCurrentPassword" className={classes.text}>
                    Current Password
                  </label>
                  <FormControl>
                    <OutlinedInput
                      fullWidth
                      className={`${classes.outlineInputField} activeText`}
                      placeholder="Current Password"
                      value={curPassword}
                      onChange={(e) => setCurPassword(e.target.value)}
                    />
                  </FormControl>
                </Grid2>
                <Grid2 container direction="column" mr={1}>
                  <label htmlFor="inputNewPassword" className={classes.text}>
                    New Password
                  </label>
                  <FormControl>
                    <OutlinedInput
                      fullWidth
                      className={`${classes.outlineInputField} activeText`}
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </FormControl>
                </Grid2>
                <Grid2 container direction="column" mr={1}>
                  <label htmlFor="inputConfirmPassword" className={classes.text}>
                    Confirm Password
                  </label>
                  <FormControl>
                    <OutlinedInput
                      fullWidth
                      className={`${classes.outlineInputField} activeText`}
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </FormControl>
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>
          <Grid2 container justifyContent="flex-end" m={3}>
            <Button
              size="small"
              variant="text"
              type="button"
              onClick={() => navigate('/dashboard')}
              className={classes.cancel}
            >
              cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              className={classes.save}
              type="submit"
              onClick={() => updateProfile()}
            >
              <div className="caption" style={{ fontSize: 'h5', textTransform: 'none' }}>
                Save Changes
              </div>
            </Button>
          </Grid2>
        </Grid2>
      </Grid2>
      {isLoading && <CircularProgress />}

      {showConfirmationModal && (
        <ConfirmationDialog
          action="RemoveProfilePic"
          confirm={handleRemoveProfilePicture}
          setShowAlert={setShowConfirmationModal}
        />
      )}
    </Grid2>
  );
};

export default EditProfile;
